import { Component, OnInit, Renderer2 } from '@angular/core';
import { EventTrackerService } from './services/event-tracker.service';
import { DataService } from './services/data.service';
import { trigger, animate, style, group, query, transition } from '@angular/animations';
import { ContentfulService } from './services/contentful.service';
import { Buffer } from 'buffer';
import { ContentfulCacheService } from './services/contentful-cache.service';
import { Router, NavigationEnd } from '@angular/router';
import { NgcCookieConsentService } from 'ngx-cookieconsent';
import { interval } from 'rxjs';
import { AuthHttpService } from './services/authentication/auth-http.service';
import * as Chart from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('routerTransition', [
      transition('* <=> *', [
        query(':enter, :leave', style({ position: 'fixed', width: '100%', opacity: 1 }), {optional: true}),
        group([
          query(':enter', [
            style({ opacity: 0 }),
            animate('400ms ease-in-out', style({ opacity: 1 }))
          ], {optional: true}),
          query(':leave', [
            style({ opacity: 1 }),
            animate('400ms ease-in-out', style({ opacity: 0 }))], {optional: true}),
        ])
      ])
    ])
  ]
})
export class AppComponent implements OnInit {
  public deviceNotOptimized = false;
  public loadingPageIndex = null;

  constructor(private eventTracker: EventTrackerService, private renderer: Renderer2, public data: DataService,
              private contentful: ContentfulService, public content: ContentfulCacheService, public router: Router,
              private ccService: NgcCookieConsentService, private authHttp: AuthHttpService) { }

  ngOnInit() {
    this.getLocales();
    this.updateLoadingPageContent();
    this.getAppEnvironmentFromUrl();
    this.handleCookieConsentPopup();
    this.getNewIdTokenEvery20Minutes();
    this._disableChartJSPluginsGlobally();
    // Initialise the event tracker service
    this.eventTracker.initialise();
    this.checkDeviceCompatibility();
    // Some browsers do not support the path prototype so define it for a click event
    if (!('path' in Event.prototype)) {
      Object.defineProperty(Event.prototype, 'path', {
        get: function() {
          const path = [];
          let currentElem = this.target;
          while (currentElem) {
            path.push(currentElem);
            currentElem = currentElem.parentElement;
          }
          if (path.indexOf(window) === -1 && path.indexOf(document) === -1) {
            path.push(document);
          }
          if (path.indexOf(window) === -1) {
            path.push(window);
          }
          return path;
        }
      });
    }
    // Add listener to the document click
    this.renderer.listen('document', 'click', (evt) => {
      // Find the closest id with track_click infront of it and then track the event
      this.closest(evt);
    });
    // Hack to include Buffer dep, broken by ng6 cli
    if (typeof window !== 'undefined') {
      window['Buffer'] = Buffer;
    }
    /*
    * The logic for showing the the lazy loading indicator is only applicable for now during app.component ngOnInit automatic login
    * The logic is separated at this stage, but a better strategy can be made for showing lazy loading indicator for the entire application
    * Especially on slower networks when a user wants to log in before the modules have completed loading
     */
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.data.lazyLoadingRoute = false;
      }
    });
    this.trackAllPageViewsWithGtag();
  }

  closest(el) {
    for (let i = 0; i < el.path.length; i++) {
      const element = el.path[i];
      if (i > 4) {
        return false;
      }
      if (typeof element.id === 'string' && element.id.indexOf('ga-track') !== -1) {
        // Track the event
        const gaElementSplit = element.id.split('_');
        this.eventTracker.gtagTrack(gaElementSplit[1], gaElementSplit[2]);
        return true;
      }
    }
  }

  /** Function to subscribe to the route and track all page views, then record them in google analytics **/
  trackAllPageViewsWithGtag() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        // Make sure no query parameters are present
        const pagePath = event.url.split('?')[0];
        this.eventTracker.gtagPageTrack(pagePath, this.getTrueUserId(), this.data.staff);
      }
    });
  }

  /** Function to get the true user id of this session, as a staff member will have 2 "users" in cache **/
  getTrueUserId(): number {
    if (this.data.staffUser) {
      return this.data.staffUser.id;
    } else if (this.data.user) {
      return this.data.user.id;
    } else {
      return null;
    }
  }

  /** Function to check whether Aeroview is compatible for mobile view **/
  checkDeviceCompatibility() {
    if (this.eventTracker.os.family === 'Android' || this.eventTracker.os.family === 'iOS') {
      // Also make sure that the width of the device is small
      if (window.innerWidth < 700) {
        this.deviceNotOptimized = true;
        this.data.mobileDevice = true;
      }
    }
  }

  /** Function to get the locales available for the app **/
  getLocales() {
    if (typeof localStorage === 'undefined') {
      this.data.selectedLanguage.code = 'en-US';
    } else {
      // Check the locale storage to make sure the user has not previously selected a certain language
      const language = localStorage.getItem('aeroLocale');
      if (language) {
        this.data.selectedLanguage.code = language;
      }
    }
    this.contentful.getLocales().then((locales) =>  {
      // Update the language options for the user to select (and sort them)
      this.data.languages = locales.items.sort(((a, b) => a.name > b.name ? 1 : -1));
      this.data.languages.forEach(item => {
        item.flag = item.code.split('-')[1] + '.svg';
      });
      this.data.updateSelectedLanguage();
      this.content.fetchAll();
    });
  }

  /** Function to update the content of the loading page **/
  updateLoadingPageContent() {
    this.content.generalPage.transformData.subscribe((transform) => {
      if (transform) {
        this.loadingPageIndex = this.content.generalPage.data.findIndex(item => item.identifier === 'loadingpage');
      }
    });
  }

  /** Function to get the environment from the app url and update the dataservice accordingly **/
  getAppEnvironmentFromUrl() {
    const hostName = window.location.hostname;
    if (hostName === 'localhost') {
      this.data.environment = hostName;
    } else {
      const subdomain = hostName.split('.')[0];
      if (
        subdomain === 'app' || // The 'app' subdomain implies our production deployment
        subdomain === 'aerobotics' // We're in the root domain, which is our production deployment
      ) {
        this.data.environment = 'www'; // Only works as long as Contentful refers to production as 'www'
      } else {
        this.data.environment = subdomain;
      }
    }
  }

  handleCookieConsentPopup() {
    // Destroy the previous message and then initialize the new one
    this.ccService.destroy();
    // Once the content is loaded, update the cookie message accordingly
    this.content.landingPageComponent.transformData.subscribe((transform) => {
      if (transform) {
        this.ccService.getConfig().content.message  = this.content.landingPageComponent.data[0].cookieConsentText;
        this.ccService.getConfig().content.dismiss = this.content.landingPageComponent.data[0].cookieConsentButton;
        this.ccService.init(this.ccService.getConfig());
        // Track the user "accepting" only if they haven't already given
        if (!(this.ccService.hasConsented())) {
          this.ccService.popupClose$.subscribe(
            () => {
              this.eventTracker.gtagTrack('navigation', 'cookie-consent');
            });
        }
      }
    });
  }

  getNewIdTokenEvery20Minutes() {
    const twentyMinuteTimer = interval(60 * 1000 * 20);
    twentyMinuteTimer.subscribe(() => {
      if (this.data.user) {
        // The response of this request is not important
        this.authHttp.get('gateway/auth/refresh_id_token/').subscribe(() => {}, () => {});
      }
    });
  }

  private _disableChartJSPluginsGlobally() {
    // Bug: https://github.com/chartjs/chartjs-plugin-datalabels/issues/134
    Chart.plugins.unregister(ChartDataLabels);
    Chart.defaults.global.plugins.datalabels.display = false;
  }

}
